export interface DiscoveryContextSource {
  type: string;
  title?: string | null;
  id?: string | null;
  detail?: string | null;
  url?: string | null;
  timestamp?: string | null;
}

export interface DiscoveryContextSummary {
  id?: string;
  name?: string | null;
  code?: string | null;
  sector?: string | null;
  status?: string | null;
  context?: string | null;
  contextGeneratedAt?: string | null;
  contextGeneratedBy?: string | null;
  contextGenerationStatus?: string | null;
  contextGenerationError?: string | null;
  sources?: DiscoveryContextSource[];
}

export interface PreDiscoverySummary {
  generatedAt?: string | null;
  generatedBy?: string | null;
  status?: string | null;
  error?: string | null;
  questions?: unknown;
}

export interface DiscoveryContextResponse {
  bundle: {
    projectId: string;
    customer: DiscoveryContextSummary;
    project: DiscoveryContextSummary;
    preDiscovery: PreDiscoverySummary;
  };
  prompt: string;
}

async function readResponsePayload<T>(
  response: Response,
): Promise<{ payload: T | null; rawText: string }> {
  const rawText = await response.text();
  if (!rawText) {
    return { payload: null, rawText: "" };
  }

  try {
    return { payload: JSON.parse(rawText) as T, rawText };
  } catch {
    return { payload: null, rawText };
  }
}

export async function fetchDiscoveryContext(projectId: string): Promise<DiscoveryContextResponse> {
  if (!projectId) {
    throw new Error("projectId is required to load discovery context");
  }

  const response = await fetch(`/api/projects/${encodeURIComponent(projectId)}/discovery/context`);
  const { payload, rawText } = await readResponsePayload<DiscoveryContextResponse & { ok?: boolean; error?: string }>(
    response,
  );

  if (!response.ok || payload?.ok === false || !payload?.bundle) {
    const fallbackText = rawText && rawText.length <= 160 ? `: ${rawText}` : "";
    const message = payload?.error ?? `Failed to load discovery context (status ${response.status})${fallbackText}`;
    throw new Error(message);
  }

  return payload;
}
